import { union } from 'lodash';
import { createSelector } from 'reselect';
import { selectors as absenceRequestsSelectors } from '../absence-requests';
import { selectors as dutyExchangesSelectors } from '../duty-exchanges';
import { selectors as dutyRequestsSelectors } from '../duty-requests';
import { selectors as extraWorkRequests } from '../extra-work-requests';
import { selectors as availabilityCodeSelectors } from '../availability-codes';
import { selectors as userSelectors } from '../users';
import { selectors as groupSelectors } from '../groups';
import { selectCurrentUserId } from '../current-user/selectors';

const nonRejectedAbsenceRequestsByMembershipId = absenceRequestsSelectors.nonRejectedAbsenceRequestsByMembershipId;
const openDutyExchangesByMembershipId = dutyExchangesSelectors.openDutyExchangesByMembershipId;

// This selector combines absence requests and duty transactions into a single data structure
export const openDutyTransactionsByMembershipId = createSelector(
  nonRejectedAbsenceRequestsByMembershipId,
  openDutyExchangesByMembershipId,
  (absenceRequestsById, dutyExchangesById) => {
    const membershipIds = union(Object.keys(absenceRequestsById), Object.keys(dutyExchangesById));
    const dutyTransactions = {};
    for (const id of membershipIds) {
      const membershipAbsenceRequests = absenceRequestsById[id] || [];
      const membershipDutyExchanges = dutyExchangesById[id] || [];
      // We add both to the duty transaction hash
      dutyTransactions[id] = [...membershipAbsenceRequests, ...membershipDutyExchanges];
    }

    return dutyTransactions;
  }
);

// Returns all duty transactions of all types, augmented with availability code,
// user, group and station
export const values = createSelector(
  absenceRequestsSelectors.values,
  dutyExchangesSelectors.values,
  dutyRequestsSelectors.values,
  extraWorkRequests.values,
  availabilityCodeSelectors.byId,
  groupSelectors.byId,
  userSelectors.byId,
  selectCurrentUserId,
  (
    absenceRequests,
    dutyExchanges,
    dutyRequests,
    extraWorkRequests,
    availabilityCodesById,
    groupsById,
    usersById,
    currentUserId
  ) => {
    const transactions = [...absenceRequests, ...dutyRequests, ...dutyExchanges, ...extraWorkRequests];

    return transactions.map(t => {
      const userInterval = (t.duty_exchange_intervals || []).find(i => i.user_id === currentUserId);
      const userState = (userInterval || {}).state || null;

      return {
        ...t,
        availabilityCode: availabilityCodesById[t.availability_code_id],
        user: usersById[t.user_id],
        station: groupsById[t.station_id],
        group: groupsById[t.group_id],
        duty_exchange_intervals: (t.duty_exchange_intervals || []).map(dti => ({
          ...dti,
          user: usersById[dti.user_id]
        })),
        userState
      };
    });
  }
);

export const augmentedDutyRequestsById = createSelector(values, dutyTransactions => {
  const result = {};
  for (const dr of dutyTransactions.filter(dt => dt.type === 'duty_request')) result[dr.id] = dr;
  return result;
});

export const selectInitialLoadComplete = createSelector(
  absenceRequestsSelectors.initialLoadComplete,
  dutyExchangesSelectors.initialLoadComplete,
  dutyRequestsSelectors.initialLoadComplete,
  (...args) => args.some(Boolean) // true if one has been loaded
);

export default {
  openDutyTransactionsByMembershipId,
  selectInitialLoadComplete,
  values
};
